import React, { useState, useEffect, useMemo } from 'react';
import Modal from './common/Modal';
import { fieldStyles } from '../config/formStyles';
import { theme } from '../config/theme';

const COMITENTE_PREFIX = '568';

const tabStyles = {
    bar: {
        display: 'flex',
        gap: '0.5rem',
        borderBottom: `1px solid ${theme.colors.border}`,
        marginBottom: '1.25rem',
    },
    tab: (isActive) => ({
        fontFamily: theme.typography.fontFamily,
        background: 'none',
        border: 'none',
        borderBottom: `2px solid ${isActive ? theme.colors.primary : 'transparent'}`,
        color: isActive ? theme.colors.primary : theme.colors.textSecondary,
        fontWeight: isActive ? '700' : '500',
        fontSize: '0.9rem',
        padding: '0.6rem 0.9rem',
        cursor: 'pointer',
        marginBottom: '-1px',
    }),
};

const listStyles = {
    wrapper: { marginTop: '1.5rem' },
    sectionTitle: {
        fontFamily: theme.typography.titleFamily,
        fontSize: '0.8rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: theme.colors.primary,
        marginBottom: '0.5rem',
    },
    list: {
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius,
        maxHeight: '260px',
        overflowY: 'auto',
        backgroundColor: theme.colors.surface,
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.75rem',
        padding: '0.5rem 0.75rem',
        borderBottom: `1px solid ${theme.colors.border}`,
    },
    rowText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
    rowName: { fontSize: '0.875rem', color: theme.colors.secondary, fontWeight: '500' },
    rowNumber: { fontSize: '0.75rem', color: theme.colors.textSecondary },
    editButton: {
        fontFamily: theme.typography.fontFamily,
        background: 'none',
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius,
        color: theme.colors.primary,
        fontSize: '0.8rem',
        fontWeight: '600',
        padding: '0.3rem 0.75rem',
        cursor: 'pointer',
        flexShrink: 0,
    },
    empty: {
        padding: '1rem 0.75rem',
        fontSize: '0.875rem',
        color: theme.colors.textSecondary,
        textAlign: 'center',
    },
    cancelEditLink: {
        background: 'none',
        border: 'none',
        color: theme.colors.errorText,
        fontSize: '0.8rem',
        fontWeight: '600',
        cursor: 'pointer',
        padding: 0,
    },
    warning: {
        fontSize: '0.78rem',
        color: theme.colors.secondary,
        backgroundColor: theme.colors.warning,
        padding: '0.5rem 0.75rem',
        borderRadius: theme.borderRadius,
    },
    inlinePrefixBox: {
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius,
        backgroundColor: theme.colors.surface,
    },
};

/**
 * Modal unificado de gestión de clientes y cuentas Jefferies.
 * Cada pestaña permite agregar registros, listarlos y editarlos (nombre y número).
 * La persistencia la resuelven los handlers del padre (onSaveClient /
 * onSaveJefferiesAccount); el listado se refresca solo vía onSnapshot.
 */
const ClientManagementModal = ({
    isOpen,
    onClose,
    clientes = [],
    cuentasJefferies = [],
    onSaveClient,
    onSaveJefferiesAccount,
}) => {
    const [activeTab, setActiveTab] = useState('clientes');

    // --- Estado pestaña Clientes ---
    const [comitenteSuffix, setComitenteSuffix] = useState('');
    const [nombre, setNombre] = useState('');
    const [editingClient, setEditingClient] = useState(null); // { id, nroComitente }
    const [clientFilter, setClientFilter] = useState('');

    // --- Estado pestaña Jefferies ---
    const [accountName, setAccountName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [editingAccount, setEditingAccount] = useState(null); // { id }
    const [jefferiesFilter, setJefferiesFilter] = useState('');

    const resetClientForm = () => {
        setComitenteSuffix('');
        setNombre('');
        setEditingClient(null);
    };

    const resetJefferiesForm = () => {
        setAccountName('');
        setAccountNumber('');
        setEditingAccount(null);
    };

    // Resetea todo al abrir el modal.
    useEffect(() => {
        if (isOpen) {
            setActiveTab('clientes');
            setClientFilter('');
            setJefferiesFilter('');
            resetClientForm();
            resetJefferiesForm();
        }
    }, [isOpen]);

    const sortedClientes = useMemo(
        () => [...clientes].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')),
        [clientes]
    );

    const sortedAccounts = useMemo(
        () => cuentasJefferies
            .filter((cuenta) => cuenta.active !== false)
            .sort((a, b) => (a.accountName || '').localeCompare(b.accountName || '')),
        [cuentasJefferies]
    );

    const filteredClientes = useMemo(() => {
        const term = clientFilter.trim().toLowerCase();
        if (!term) return sortedClientes;
        return sortedClientes.filter((c) =>
            (c.nombre || '').toLowerCase().includes(term) ||
            (c.nroComitente || '').includes(term)
        );
    }, [sortedClientes, clientFilter]);

    const filteredAccounts = useMemo(() => {
        const term = jefferiesFilter.trim().toLowerCase();
        if (!term) return sortedAccounts;
        return sortedAccounts.filter((a) =>
            (a.accountName || '').toLowerCase().includes(term) ||
            (a.accountNumber || '').toLowerCase().includes(term)
        );
    }, [sortedAccounts, jefferiesFilter]);

    const handleEditClient = (client) => {
        setEditingClient({ id: client.id, nroComitente: client.nroComitente });
        setComitenteSuffix((client.nroComitente || '').replace(COMITENTE_PREFIX, '').slice(0, 6));
        setNombre(client.nombre || '');
    };

    const handleEditAccount = (account) => {
        setEditingAccount({ id: account.id });
        setAccountName(account.accountName || '');
        setAccountNumber(account.accountNumber || '');
    };

    const handleSubmitClient = (e) => {
        e.preventDefault();
        if (comitenteSuffix.length !== 6) {
            alert('Por favor, complete los 6 dígitos del número de comitente.');
            return;
        }
        const nroComitenteCompleto = `${COMITENTE_PREFIX}${comitenteSuffix}`;
        onSaveClient(nroComitenteCompleto, nombre.trim(), editingClient ? editingClient.id : null);
        resetClientForm();
    };

    const handleSubmitAccount = (e) => {
        e.preventDefault();
        const name = accountName.trim();
        const number = accountNumber.trim();
        if (!name || !number) {
            alert('Por favor, complete el nombre y el número de cuenta.');
            return;
        }
        onSaveJefferiesAccount(name, number, editingAccount ? editingAccount.id : null);
        resetJefferiesForm();
    };

    const isEditingClient = Boolean(editingClient);
    const isEditingAccount = Boolean(editingAccount);

    const footer = (
        <button
            type="button"
            onClick={onClose}
            style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }}
        >
            Cerrar
        </button>
    );

    return (
        <Modal isOpen={isOpen} title="Gestión de Clientes" maxWidth="560px" footer={footer}>
            <div style={tabStyles.bar}>
                <button
                    type="button"
                    style={tabStyles.tab(activeTab === 'clientes')}
                    onClick={() => setActiveTab('clientes')}
                >
                    Clientes
                </button>
                <button
                    type="button"
                    style={tabStyles.tab(activeTab === 'jefferies')}
                    onClick={() => setActiveTab('jefferies')}
                >
                    Cuentas Jefferies
                </button>
            </div>

            {activeTab === 'clientes' && (
                <div>
                    <form onSubmit={handleSubmitClient} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={listStyles.sectionTitle}>
                                {isEditingClient ? `Editando ${editingClient.nroComitente}` : 'Agregar cliente'}
                            </span>
                            {isEditingClient && (
                                <button type="button" onClick={resetClientForm} style={listStyles.cancelEditLink}>
                                    Cancelar edición
                                </button>
                            )}
                        </div>

                        {isEditingClient && (
                            <p style={listStyles.warning}>
                                El nro de comitente vincula saldos, operaciones, cauciones e instrucciones.
                                Si lo cambiás, los movimientos históricos ya cargados conservan el número anterior.
                            </p>
                        )}

                        <div>
                            <label style={fieldStyles.label}>Nro de Comitente</label>
                            <div style={listStyles.inlinePrefixBox}>
                                <span style={{ paddingLeft: '0.75rem', color: theme.colors.textSecondary, userSelect: 'none' }}>
                                    {COMITENTE_PREFIX}
                                </span>
                                <input
                                    type="text"
                                    value={comitenteSuffix}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^[0-9]*$/.test(value) && value.length <= 6) {
                                            setComitenteSuffix(value);
                                        }
                                    }}
                                    style={{ ...fieldStyles.input, border: 'none', flex: 1, paddingLeft: '0.5rem' }}
                                    required
                                    placeholder="xxxxxx"
                                />
                            </div>
                        </div>

                        <div>
                            <label style={fieldStyles.label}>Nombre y Apellido / Razón Social</label>
                            <input
                                type="text"
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                style={fieldStyles.input}
                                required
                            />
                        </div>

                        <button type="submit" style={{ ...fieldStyles.button, alignSelf: 'flex-start' }}>
                            {isEditingClient ? 'Guardar cambios' : 'Agregar cliente'}
                        </button>
                    </form>

                    <div style={listStyles.wrapper}>
                        <div style={listStyles.sectionTitle}>Clientes cargados ({sortedClientes.length})</div>
                        <input
                            type="text"
                            value={clientFilter}
                            onChange={(e) => setClientFilter(e.target.value)}
                            style={{ ...fieldStyles.input, marginBottom: '0.5rem' }}
                            placeholder="Buscar por nombre o número…"
                        />
                        <div style={listStyles.list}>
                            {filteredClientes.length === 0 ? (
                                <div style={listStyles.empty}>No hay clientes para mostrar.</div>
                            ) : (
                                filteredClientes.map((client) => (
                                    <div key={client.id} style={listStyles.row}>
                                        <div style={listStyles.rowText}>
                                            <span style={listStyles.rowName}>{client.nombre}</span>
                                            <span style={listStyles.rowNumber}>{client.nroComitente}</span>
                                        </div>
                                        <button
                                            type="button"
                                            style={listStyles.editButton}
                                            onClick={() => handleEditClient(client)}
                                        >
                                            Editar
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'jefferies' && (
                <div>
                    <form onSubmit={handleSubmitAccount} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={listStyles.sectionTitle}>
                                {isEditingAccount ? 'Editando cuenta Jefferies' : 'Agregar cuenta Jefferies'}
                            </span>
                            {isEditingAccount && (
                                <button type="button" onClick={resetJefferiesForm} style={listStyles.cancelEditLink}>
                                    Cancelar edición
                                </button>
                            )}
                        </div>

                        {isEditingAccount && (
                            <p style={listStyles.warning}>
                                El account number vincula los movimientos de la cuenta. Si lo cambiás, los
                                registros históricos ya cargados conservan el número anterior.
                            </p>
                        )}

                        <div>
                            <label style={fieldStyles.label}>Account Name (Titular)</label>
                            <input
                                type="text"
                                value={accountName}
                                onChange={(e) => setAccountName(e.target.value)}
                                style={fieldStyles.input}
                                placeholder="NAVIO 1 CORP"
                                required
                            />
                        </div>

                        <div>
                            <label style={fieldStyles.label}>Account Number</label>
                            <input
                                type="text"
                                value={accountNumber}
                                onChange={(e) => setAccountNumber(e.target.value.toUpperCase())}
                                style={fieldStyles.input}
                                placeholder="JW3165891"
                                required
                            />
                        </div>

                        <button type="submit" style={{ ...fieldStyles.button, alignSelf: 'flex-start' }}>
                            {isEditingAccount ? 'Guardar cambios' : 'Agregar cuenta'}
                        </button>
                    </form>

                    <div style={listStyles.wrapper}>
                        <div style={listStyles.sectionTitle}>Cuentas cargadas ({sortedAccounts.length})</div>
                        <input
                            type="text"
                            value={jefferiesFilter}
                            onChange={(e) => setJefferiesFilter(e.target.value)}
                            style={{ ...fieldStyles.input, marginBottom: '0.5rem' }}
                            placeholder="Buscar por titular o número…"
                        />
                        <div style={listStyles.list}>
                            {filteredAccounts.length === 0 ? (
                                <div style={listStyles.empty}>No hay cuentas para mostrar.</div>
                            ) : (
                                filteredAccounts.map((account) => (
                                    <div key={account.id} style={listStyles.row}>
                                        <div style={listStyles.rowText}>
                                            <span style={listStyles.rowName}>{account.accountName}</span>
                                            <span style={listStyles.rowNumber}>{account.accountNumber}</span>
                                        </div>
                                        <button
                                            type="button"
                                            style={listStyles.editButton}
                                            onClick={() => handleEditAccount(account)}
                                        >
                                            Editar
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default ClientManagementModal;
